import { MutableRefObject, useMemo, useState } from 'react';
import { useAppSelector } from '../../redux/ReduxHooks';
import * as monaco from 'monaco-editor';
import { insertSnippetAtLine, uniquifyVariableName } from '../../Components/CodeEditor/utils/snippetInsertion';
import type { PendingActionDrop } from '../../Components/CodeEditor/components/ActionArgumentsDialog';
import type { CodeSnippetImport } from '../../Components/CodeEditor/types';
import type { PendingDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import type { Path } from '../../Components/PathBuilder/pathBuilderTypes';
import { DUPLICATE_DRIVE_BASE_DROP_MESSAGE } from '../../Components/CodeEditor/utils/driveBaseSingleton';

interface UseEditorDialogsParams {
  standaloneEditorRef: MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  debouncedSync: (code: string) => void;
}

export function useEditorDialogs({ standaloneEditorRef, debouncedSync }: UseEditorDialogsParams) {
  const project = useAppSelector((state) => state.project.loadedProject);

  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [selectedPathName, setSelectedPathName] = useState('');
  const [pendingPathDrop, setPendingPathDrop] = useState<{ code: string; targetLine: number } | null>(null);
  const [noPathsDialogOpen, setNoPathsDialogOpen] = useState(false);

  const [actionArgsDialogOpen, setActionArgsDialogOpen] = useState(false);
  const [pendingActionDrop, setPendingActionDrop] = useState<PendingActionDrop | null>(null);

  const [deviceSnippetPortDialogOpen, setDeviceSnippetPortDialogOpen] = useState(false);
  const [pendingDeviceSnippetPortDrop, setPendingDeviceSnippetPortDrop] = useState<(PendingDeviceSnippetPortDrop & { targetLine: number }) | null>(
    null
  );
  const [duplicateDriveBaseDialogOpen, setDuplicateDriveBaseDialogOpen] = useState(false);
  const pathOptions = useMemo<Array<{ id: string; name: string }>>(
    () => (project?.paths ?? []).map((p: Path) => ({ id: p.id, name: p.name })),
    [project?.paths]
  );

  const handlePathDialogClose = () => {
    setPathDialogOpen(false);
    setPendingPathDrop(null);
  };

  const handlePathDialogConfirm = () => {
    const editorInst = standaloneEditorRef.current;
    if (!editorInst || !pendingPathDrop || !selectedPathName) return;
    const model = editorInst.getModel();
    if (!model) return;

    let filled = pendingPathDrop.code;
    if (/\bpath\s*=\s*(?=[,)]|$)/.test(filled)) {
      filled = filled.replace(/\bpath\s*=\s*(?=[,)]|$)/g, `path="${selectedPathName}"`);
    } else {
      filled = filled.replace(/\bname\s*=\s*(?=[,)]|$)/g, `name="${selectedPathName}"`);
    }

    insertSnippetAtLine({
      editor: editorInst,
      textToInsert: filled,
      targetLine: pendingPathDrop.targetLine,
    });

    try {
      debouncedSync(model.getValue());
    } catch {
      // Ignore sync errors
    }

    setPathDialogOpen(false);
    setPendingPathDrop(null);
    setSelectedPathName('');
  };

  const handleNoPathsDialogClose = () => {
    setNoPathsDialogOpen(false);
  };

  const requestPathDrop = (code: string, targetLine: number) => {
    setPendingPathDrop({ code, targetLine });
    setSelectedPathName('');
    setPathDialogOpen(true);
  };

  const requestNoPathsDialog = () => {
    setNoPathsDialogOpen(true);
    setPendingPathDrop(null);
  };

  const insertSnippetWithUniquify = (code: string, targetLine: number, imports?: CodeSnippetImport[]) => {
    const editorInst = standaloneEditorRef.current;
    if (!editorInst) return;
    const model = editorInst.getModel();
    if (!model) return;

    const textToInsert = uniquifyVariableName(code, model);
    insertSnippetAtLine({
      editor: editorInst,
      textToInsert,
      targetLine,
      imports,
    });

    try {
      debouncedSync(model.getValue());
    } catch {
      // Ignore sync errors
    }
  };

  const insertActionCode = (code: string, targetLine: number) => {
    insertSnippetWithUniquify(code, targetLine);
  };

  const handleActionArgsDialogClose = () => {
    if (pendingActionDrop) {
      insertActionCode(pendingActionDrop.code, pendingActionDrop.targetLine);
    }
    setActionArgsDialogOpen(false);
    setPendingActionDrop(null);
  };

  const handleActionArgsDialogConfirm = (filledCode: string) => {
    if (pendingActionDrop) {
      insertActionCode(filledCode, pendingActionDrop.targetLine);
    }
    setActionArgsDialogOpen(false);
    setPendingActionDrop(null);
  };

  const requestActionArgsDrop = (drop: PendingActionDrop) => {
    setPendingActionDrop(drop);
    setActionArgsDialogOpen(true);
  };

  const requestDeviceSnippetPortDrop = (drop: PendingDeviceSnippetPortDrop & { targetLine: number }) => {
    setPendingDeviceSnippetPortDrop(drop);
    setDeviceSnippetPortDialogOpen(true);
  };

  const handleDeviceSnippetPortDialogClose = () => {
    setDeviceSnippetPortDialogOpen(false);
    setPendingDeviceSnippetPortDrop(null);
  };

  const handleDeviceSnippetPortDialogConfirm = (filledCode: string) => {
    if (pendingDeviceSnippetPortDrop) {
      insertSnippetWithUniquify(filledCode, pendingDeviceSnippetPortDrop.targetLine, pendingDeviceSnippetPortDrop.imports);
    }
    setDeviceSnippetPortDialogOpen(false);
    setPendingDeviceSnippetPortDrop(null);
  };

  const requestDuplicateDriveBaseDialog = () => {
    setDuplicateDriveBaseDialogOpen(true);
  };

  const handleDuplicateDriveBaseDialogClose = () => {
    setDuplicateDriveBaseDialogOpen(false);
  };

  return {
    pathDialogOpen,
    selectedPathName,
    setSelectedPathName,
    pendingPathDrop,
    noPathsDialogOpen,
    handlePathDialogClose,
    handlePathDialogConfirm,
    handleNoPathsDialogClose,
    requestPathDrop,
    requestNoPathsDialog,
    paths: project?.paths || [],
    actionArgsDialogOpen,
    pendingActionDrop,
    pathOptions,
    handleActionArgsDialogClose,
    handleActionArgsDialogConfirm,
    requestActionArgsDrop,
    deviceSnippetPortDialogOpen,
    pendingDeviceSnippetPortDrop,
    handleDeviceSnippetPortDialogClose,
    handleDeviceSnippetPortDialogConfirm,
    requestDeviceSnippetPortDrop,
    duplicateDriveBaseDialogOpen,
    duplicateDriveBaseMessage: DUPLICATE_DRIVE_BASE_DROP_MESSAGE,
    handleDuplicateDriveBaseDialogClose,
    requestDuplicateDriveBaseDialog,
  };
}
